import { useMemo, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faRotateLeft,
  faXmark,
  faFloppyDisk,
  faSpinner,
  faCheckCircle,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseDateStr, formatDateStr } from "../utils";
import { calculateKpiMeasure } from "../kpiMeasureUtils";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "36px",
    fontSize: "0.8125rem",
    borderColor: state.isFocused ? "#144677" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #144677" : "none",
    "&:hover": { borderColor: "#144677" },
    backgroundColor: "white",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8125rem",
    padding: "6px 10px",
    backgroundColor: state.isSelected
      ? "#144677"
      : state.isFocused
      ? "#e8f0fe"
      : "white",
  }),
  dropdownIndicator: (base) => ({ ...base, padding: "2px 6px" }),
  clearIndicator: (base) => ({ ...base, padding: "2px 4px" }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({ ...base, fontSize: "0.8125rem" }),
  placeholder: (base) => ({ ...base, fontSize: "0.8125rem", color: "#9ca3af" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const OTHER_VALUE = "__other__";
const OTHER_OPTION = { value: OTHER_VALUE, label: "Other" };

const initialBulkData = {
  etaDate: "",
  etaAdjustDays: "",
  completedDate: "",
  completedAdjustDays: "",
  kpiMeasure: "",
  reasonId: null,
  reason: null,
  kpiHit: null,
};

const buildBulkUpdates = (bulkData, initialData = initialBulkData) => {
  const updates = {};
  if (bulkData.etaDate && bulkData.etaDate !== initialData.etaDate) {
    updates.etaDate = bulkData.etaDate;
  }
  if (
    bulkData.etaAdjustDays !== "" &&
    bulkData.etaAdjustDays !== initialData.etaAdjustDays
  ) {
    updates.etaAdjustDays = Number(bulkData.etaAdjustDays);
  }
  if (
    bulkData.completedDate &&
    bulkData.completedDate !== initialData.completedDate
  ) {
    updates.completedDate = bulkData.completedDate;
  }
  if (
    bulkData.completedAdjustDays !== "" &&
    bulkData.completedAdjustDays !== initialData.completedAdjustDays
  ) {
    updates.completedAdjustDays = Number(bulkData.completedAdjustDays);
  }
  if (
    bulkData.kpiMeasure.trim() &&
    bulkData.kpiMeasure.trim() !== (initialData.kpiMeasure || "").trim()
  ) {
    updates.kpiMeasure = bulkData.kpiMeasure.trim();
  }
  if (
    bulkData.reasonId !== initialData.reasonId &&
    bulkData.reasonId !== null &&
    bulkData.reasonId !== OTHER_VALUE
  ) {
    updates.reasonId = bulkData.reasonId;
  }
  if (bulkData.reason !== initialData.reason && bulkData.reason !== null) {
    updates.reason = bulkData.reason;
  }
  if (bulkData.kpiHit !== initialData.kpiHit && bulkData.kpiHit !== null) {
    updates.kpiHit = bulkData.kpiHit;
  }
  return updates;
};

export default function BulkUpdatePanel({
  isOpen,
  selectedCount,
  selectedConsignments,
  isApplying = false,
  reasonOptions = [],
  isReasonOptionsLoading = false,
  onApply,
  onCancel,
}) {
  const [bulkData, setBulkData] = useState(initialBulkData);
  const [initialFormData, setInitialFormData] = useState(initialBulkData);
  const [isKpiHitManuallyEdited, setIsKpiHitManuallyEdited] = useState(false);
  const [otherText, setOtherText] = useState("");
  const singleSelectedConsignment =
    selectedConsignments.length === 1 ? selectedConsignments[0] : null;

  const resetBulkData = () => {
    setBulkData(initialFormData);
    setIsKpiHitManuallyEdited(false);
    setOtherText("");
  };

  useEffect(() => {
    if (!isOpen) return;

    const nextInitialData = singleSelectedConsignment
      ? buildInitialBulkData(singleSelectedConsignment)
      : initialBulkData;
    const nextCalculatedKpiHit =
      nextInitialData.kpiMeasure !== ""
        ? Number(nextInitialData.kpiMeasure) <= 0
        : null;
    const shouldUseManualKpiHit =
      singleSelectedConsignment &&
      (singleSelectedConsignment.kpiHit === true ||
        singleSelectedConsignment.kpiHit === false) &&
      nextCalculatedKpiHit !== null &&
      singleSelectedConsignment.kpiHit !== nextCalculatedKpiHit;

    setInitialFormData(nextInitialData);
    setBulkData(nextInitialData);
    setIsKpiHitManuallyEdited(Boolean(shouldUseManualKpiHit));
    setOtherText("");
  }, [isOpen, singleSelectedConsignment]);

  useEffect(() => {
    if (!isOpen) {
      setInitialFormData(initialBulkData);
      setBulkData(initialBulkData);
      setIsKpiHitManuallyEdited(false);
      setOtherText("");
    }
  }, [isOpen]);

  const updatesToApply = useMemo(
    () => buildBulkUpdates(bulkData, initialFormData),
    [bulkData, initialFormData]
  );
  const pendingFieldCount = Object.keys(updatesToApply).length;
  const selectedReasonOption = useMemo(() => {
    if (bulkData.reasonId === OTHER_VALUE) return OTHER_OPTION;

    if (bulkData.reasonId !== null) {
      return (
        reasonOptions.find((reason) => reason.value === bulkData.reasonId) ?? {
          value: bulkData.reasonId,
          label: bulkData.reasonId,
          isValid: false,
        }
      );
    }

    if (bulkData.reason !== null) {
      return {
        value: bulkData.reason,
        label: bulkData.reason,
        isValid: false,
        __isNew__: true,
      };
    }

    return null;
  }, [bulkData.reason, bulkData.reasonId, reasonOptions]);

  const isOtherSelected = bulkData.reasonId === OTHER_VALUE;

  const set = (key, val) => setBulkData((p) => ({ ...p, [key]: val }));
  const setReason = (option) => {
    if (!option) {
      setBulkData((p) => ({ ...p, reasonId: null, reason: null }));
      setOtherText("");
      return;
    }

    if (option.value === OTHER_VALUE) {
      setBulkData((p) => ({ ...p, reasonId: OTHER_VALUE, reason: null }));
      setOtherText("");
      return;
    }

    if (option.__isNew__) {
      setBulkData((p) => ({ ...p, reasonId: option.value, reason: null }));
      setOtherText("");
      return;
    }

    setBulkData((p) => ({ ...p, reasonId: option.value, reason: null }));
    setOtherText("");
  };

  const handleOtherTextChange = (val) => {
    setOtherText(val);
    setBulkData((p) => ({ ...p, reason: val.trim() ? `Other - ${val.trim()}` : null }));
  };

  const handleApply = async () => {
    if (pendingFieldCount === 0) return;
    const wasSuccessful = await onApply(updatesToApply);
    if (wasSuccessful) {
      resetBulkData();
    }
  };

  const etaDateObj = parseDateStr(bulkData.etaDate);
  const completedDateObj = parseDateStr(bulkData.completedDate);
  const isEtaDateMode = !!bulkData.etaDate;
  const isEtaAdjustMode = bulkData.etaAdjustDays !== "";
  const isCompletedDateMode = !!bulkData.completedDate;
  const isCompletedAdjustMode = bulkData.completedAdjustDays !== "";
  const isSingleSelection = !!singleSelectedConsignment;
  const hasInitialEtaDate = !!initialFormData.etaDate;
  const hasInitialCompletedDate = !!initialFormData.completedDate;
  const isEtaAdjustDisabled =
    isEtaDateMode || (isSingleSelection && !hasInitialEtaDate);
  const isCompletedAdjustDisabled =
    isCompletedDateMode || (isSingleSelection && !hasInitialCompletedDate);
  const calculatedKpiMeasure = useMemo(
    () =>
      bulkData.etaDate &&
      bulkData.completedDate &&
      !isEtaAdjustMode &&
      !isCompletedAdjustMode
        ? calculateKpiMeasure({
            etaDate: bulkData.etaDate,
            completedDate: bulkData.completedDate,
          })
        : "",
    [bulkData.etaDate, bulkData.completedDate, isEtaAdjustMode, isCompletedAdjustMode]
  );
  const calculatedKpiHit = useMemo(() => {
    if (calculatedKpiMeasure === "" || calculatedKpiMeasure === null) return null;
    const numericValue = Number(calculatedKpiMeasure);
    if (Number.isNaN(numericValue)) return null;
    return numericValue <= 0;
  }, [calculatedKpiMeasure]);

  useEffect(() => {
    setBulkData((prev) =>
      prev.kpiMeasure === (calculatedKpiMeasure || "")
        ? prev
        : { ...prev, kpiMeasure: calculatedKpiMeasure || "" }
    );
  }, [calculatedKpiMeasure]);

  useEffect(() => {
    if (!isKpiHitManuallyEdited) {
      setBulkData((prev) =>
        prev.kpiHit === calculatedKpiHit
          ? prev
          : { ...prev, kpiHit: calculatedKpiHit }
      );
    }
  }, [calculatedKpiHit, isKpiHitManuallyEdited]);

  const handleDateChange = (key, date) => {
    setBulkData((prev) => ({
      ...prev,
      [key]: date ? formatDateStr(date) : "",
      [key === "etaDate" ? "etaAdjustDays" : "completedAdjustDays"]: "",
    }));
  };

  const handleAdjustDaysChange = (dateKey, adjustKey, value) => {
    setBulkData((prev) => ({
      ...prev,
      [dateKey]: "",
      [adjustKey]: value,
    }));
  };

  const clearDateField = (dateKey) => {
    setBulkData((prev) => ({ ...prev, [dateKey]: "" }));
  };

  const clearAdjustField = (adjustKey) => {
    setBulkData((prev) => ({ ...prev, [adjustKey]: "" }));
  };

  const handleKpiHitChange = (value, manual = true) => {
    setIsKpiHitManuallyEdited(manual);
    set("kpiHit", value);
  };

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-[110] bg-black/25 backdrop-blur-[2px] transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => {
          if (!isApplying) onCancel();
        }}
      />

      {/* ── Drawer ────────────────────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[120] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#144677] to-[#1a5691] px-5 pt-5 pb-4 flex-shrink-0">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-base leading-tight">
                  Edit
                </p>
                <p className="text-white/60 text-xs mt-0.5">
                  {selectedCount} consignment{selectedCount !== 1 ? "s" : ""} selected
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              disabled={isApplying}
              className="text-white/60 hover:text-white p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
              title="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          </div>

          {/* Selected MS chips */}
          <div className="flex flex-wrap gap-1.5 max-h-[72px] overflow-y-auto pr-1 custom-scrollbar">
            {selectedConsignments.map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/15 hover:bg-white/20 text-white text-[10px] font-semibold rounded-full transition-colors cursor-default"
              >
                <FontAwesomeIcon icon={faChevronRight} className="w-2 h-2 opacity-60" />
                {c.msNumber}
              </span>
            ))}
          </div>
        </div>

        {/* Pending indicator */}
        {pendingFieldCount > 0 && (
          <div className="flex items-center gap-2 px-5 py-2 bg-amber-50 border-b border-amber-100 flex-shrink-0">
            <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 text-amber-500" />
            <span className="text-xs font-medium text-amber-700">
              {pendingFieldCount} field{pendingFieldCount !== 1 ? "s" : ""} ready to apply
            </span>
            <button
              disabled={isApplying}
              onClick={resetBulkData}
              className="ml-auto text-[10px] font-semibold text-amber-600 hover:text-amber-800 flex items-center gap-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faRotateLeft} className="w-2.5 h-2.5" />
              Reset
            </button>
          </div>
        )}

        {/* ── Scrollable body ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 custom-scrollbar">
          <Section title="Bulk Update">
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_110px]">
                <div className="difot-datepicker-wrapper">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-xs font-semibold text-gray-600">
                      ETA
                    </label>
                    {isEtaDateMode ? (
                      <button
                        type="button"
                        onClick={() => clearDateField("etaDate")}
                        className="text-[11px] font-semibold text-[#144677] hover:underline"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <DatePicker
                    selected={etaDateObj}
                    onChange={(d) => handleDateChange("etaDate", d)}
                    disabled={isEtaAdjustMode}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/YYYY"
                    className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] ${isEtaAdjustMode ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                    popperProps={{ strategy: "fixed" }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-xs font-semibold text-gray-600">
                      Adjust Days
                    </label>
                    {isEtaAdjustMode ? (
                      <button
                        type="button"
                        onClick={() => clearAdjustField("etaAdjustDays")}
                        className="text-[11px] font-semibold text-[#144677] hover:underline"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <input
                    type="number"
                    value={bulkData.etaAdjustDays}
                    onChange={(e) =>
                      handleAdjustDaysChange("etaDate", "etaAdjustDays", e.target.value)
                    }
                    disabled={isEtaAdjustDisabled}
                    placeholder="e.g. -2, 0, 3"
                    className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#144677] ${isEtaAdjustDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                  />
                  {isSingleSelection && !hasInitialEtaDate ? (
                    <p className="mt-1 text-[11px] text-gray-400">
                      Adjust Days is disabled because the original ETA Date is missing.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_110px]">
                <div className="difot-datepicker-wrapper">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-xs font-semibold text-gray-600">
                      Completed Date
                    </label>
                    {isCompletedDateMode ? (
                      <button
                        type="button"
                        onClick={() => clearDateField("completedDate")}
                        className="text-[11px] font-semibold text-[#144677] hover:underline"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <DatePicker
                    selected={completedDateObj}
                    onChange={(d) => handleDateChange("completedDate", d)}
                    disabled={isCompletedAdjustMode}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/YYYY"
                    className={`w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] ${isCompletedAdjustMode ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                    popperProps={{ strategy: "fixed" }}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="block text-xs font-semibold text-gray-600">
                      Adjust Days
                    </label>
                    {isCompletedAdjustMode ? (
                      <button
                        type="button"
                        onClick={() => clearAdjustField("completedAdjustDays")}
                        className="text-[11px] font-semibold text-[#144677] hover:underline"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                  <input
                    type="number"
                    value={bulkData.completedAdjustDays}
                    onChange={(e) =>
                      handleAdjustDaysChange(
                        "completedDate",
                        "completedAdjustDays",
                        e.target.value
                      )
                    }
                    disabled={isCompletedAdjustDisabled}
                    placeholder="e.g. -2, 0, 3"
                    className={`w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#144677] ${isCompletedAdjustDisabled ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""}`}
                  />
                  {isSingleSelection && !hasInitialCompletedDate ? (
                    <p className="mt-1 text-[11px] text-gray-400">
                      Adjust Days is disabled because the original Complete Date is missing.
                    </p>
                  ) : null}
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold text-[#f28c28]">
                  KPI Measure
                </label>
                <input
                  type="text"
                  readOnly
                  value={formatKpiMeasureDisplay(bulkData.kpiMeasure)}
                  className="w-full rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:outline-none"
                />
                {(isEtaAdjustMode || isCompletedAdjustMode) && (
                  <p className="mt-1 text-[11px] text-gray-400">
                    KPI Measure will be finalized by the API when adjust days are used.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#f28c28] mb-2">
                  KPI Hit
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <ToggleBtn
                    active={!isKpiHitManuallyEdited}
                    onClick={() => handleKpiHitChange(calculatedKpiHit, false)}
                    neutral
                  >
                    Auto
                  </ToggleBtn>
                  <ToggleBtn
                    active={isKpiHitManuallyEdited && bulkData.kpiHit === true}
                    onClick={() => handleKpiHitChange(true)}
                    color="emerald"
                  >
                    Yes
                  </ToggleBtn>
                  <ToggleBtn
                    active={isKpiHitManuallyEdited && bulkData.kpiHit === false}
                    onClick={() => handleKpiHitChange(false)}
                    color="red"
                  >
                    No
                  </ToggleBtn>
                </div>
                <p className="mt-1 text-[11px] text-gray-400">
                  Auto rule: KPI Hit = Yes if KPI Measure is 0 or less.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-2">
                  Reason
                </label>
                <CreatableSelect
                  options={[
                    ...reasonOptions,
                    OTHER_OPTION,
                  ]}
                  value={selectedReasonOption}
                  onChange={setReason}
                  isClearable
                  isLoading={isReasonOptionsLoading}
                  placeholder="Select reason..."
                  formatCreateLabel={(input) => `Use custom reason "${input}"`}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {isOtherSelected && (
                  <input
                    type="text"
                    value={otherText}
                    onChange={(e) => handleOtherTextChange(e.target.value)}
                    placeholder='Describe the reason...'
                    className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#144677]"
                    autoFocus
                  />
                )}
              </div>
            </div>
          </Section>
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              disabled={isApplying}
              className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={pendingFieldCount === 0 || isApplying}
              className="flex-[2] flex items-center justify-center gap-2 py-2.5 text-sm font-bold text-white bg-[#144677] hover:bg-[#1a5691] rounded-xl transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon
                icon={isApplying ? faSpinner : faFloppyDisk}
                className={`w-3.5 h-3.5 ${isApplying ? "animate-spin" : ""}`}
              />
              {isApplying
                ? "Saving..."
                : `Apply to ${selectedCount} item${selectedCount !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
          {title}
        </span>
        <div className="flex-1 h-px bg-gray-100" />
      </div>
      {children}
    </div>
  );
}

function ToggleBtn({ active, onClick, children, color, neutral, icon }) {
  const activeColors = {
    emerald: "bg-emerald-500 text-white border-emerald-500 shadow-sm",
    red: "bg-red-500 text-white border-red-500 shadow-sm",
    amber: "bg-amber-500 text-white border-amber-500 shadow-sm",
  };
  const inactiveColors = "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:bg-gray-50";
  const neutralActive = "bg-gray-200 text-gray-700 border-gray-200 shadow-sm";

  const cls = active
    ? neutral
      ? neutralActive
      : (activeColors[color] ?? neutralActive)
    : inactiveColors;

  return (
    <button
      onClick={onClick}
      className={`py-2 text-xs font-semibold border rounded-lg transition-all duration-150 flex items-center justify-center gap-1.5 ${cls}`}
    >
      {icon && <FontAwesomeIcon icon={icon} className="w-3 h-3" />}
      {children}
    </button>
  );
}

function buildInitialBulkData(consignment) {
  const etaDate = normalizeConsignmentDate(
    consignment?.etaDate || consignment?.eta || ""
  );
  const completedDate = normalizeConsignmentDate(
    consignment?.completedDate || ""
  );
  const calculatedKpiMeasure =
    etaDate && completedDate
      ? calculateKpiMeasure({ etaDate, completedDate })
      : "";
  const existingKpiMeasure = normalizeTextValue(consignment?.kpiMeasure);
  const existingKpiHit =
    consignment?.kpiHit === true || consignment?.kpiHit === false
      ? consignment.kpiHit
      : null;

  return {
    ...initialBulkData,
    etaDate,
    completedDate,
    kpiHit:
      existingKpiHit !== null
        ? existingKpiHit
        : calculatedKpiMeasure !== ""
        ? Number(calculatedKpiMeasure) <= 0
        : null,
    kpiMeasure: existingKpiMeasure || calculatedKpiMeasure || "",
    reasonId: normalizeTextValue(consignment?.reasonId) || null,
    reason: consignment?.reasonId ? null : normalizeTextValue(consignment?.reason) || null,
  };
}

function normalizeTextValue(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function normalizeConsignmentDate(value) {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;

  const datePart = raw.split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  }

  return raw;
}

function formatKpiMeasureDisplay(value) {
  const raw = normalizeTextValue(value);
  if (!raw) return "Will be calculated";
  const numericValue = Number(raw);
  if (Number.isNaN(numericValue)) return raw;
  if (numericValue <= 0) {
    return `${numericValue} days (On Time)`;
  }
  return `${numericValue} days (Late)`;
}
